// client/src/components/PropertyTabs.js

import React from "react";
import { TrendingUp } from "lucide-react";

// Correct imports based on your file structure
import OverviewTab from "./Overview/OverviewTab.js";
import DocumentsTab from "./Documents/DocumentsTab.js";
import FinancesTab from "./Finances/FinancesTab.js";
import HomeRunTab from "./HomeRunTab.js";
import UsersTab from "./UsersTab.js";
import CommunicationTab from "./CommunicationTab.js";
import PropertyMap from "./Shared/PropertyMap.jsx";

export default function PropertyTabs({ property, metrics, activeTab, onTabChange }) {
  const setActiveTab = onTabChange;

  if (!property) {
    return <div className="text-gray-500 p-4">Loading…</div>;
  }

  return (
    <div className="space-y-0">
      {/* TAB BUTTONS - Floating Design */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-2">
        <div className="flex gap-2 overflow-x-auto">

          <button
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === "overview"
                ? "bg-hn-red text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>

          <button
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === "documents"
                ? "bg-hn-red text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("documents")}
          >
            Documents
          </button>

          <button
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === "finances"
                ? "bg-hn-red text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("finances")}
          >
            Finances
          </button>

          <button
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === "homerun"
                ? "bg-hn-red text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("homerun")}
          >
            Home Run
          </button>

          <button
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === "users"
                ? "bg-hn-red text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("users")}
          >
            Users
          </button>

          <button
            className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === "communication"
                ? "bg-hn-red text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTab("communication")}
          >
            Communication
          </button>
        </div>
      </div>

      {/* KEY METRICS CARDS */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">

          {/* VALUATION */}
          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
            <p className="font-semibold text-blue-900 text-xs uppercase tracking-wide">Current Value</p>
            <p className="text-2xl font-bold mt-2 text-blue-900">
              ${metrics.currentValue?.toLocaleString() || "N/A"}
            </p>
            <p className="text-xs text-blue-700 mt-1 flex items-center gap-1">
              {metrics.valuation?.trend || "Stable"} <TrendingUp className="w-3 h-3" />
            </p>
          </div>

          {/* MORTGAGE */}
          <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200">
            <p className="font-semibold text-red-900 text-xs uppercase tracking-wide">Mortgage</p>
            <p className="text-2xl font-bold mt-2 text-red-900">
              {metrics.mortgage?.balance ? `$${(metrics.mortgage.balance / 1000).toFixed(0)}K` : "None"}
            </p>
            <p className="text-xs text-red-700 mt-1">
              {metrics.mortgage?.rate ? `${metrics.mortgage.rate}% APR` : "Owned outright"}
            </p>
          </div>

          {/* EQUITY */}
          <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
            <p className="font-semibold text-emerald-900 text-xs uppercase tracking-wide">Equity</p>
            <p className="text-2xl font-bold mt-2 text-emerald-900">
              ${metrics.equity?.toLocaleString() || "0"}
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              {metrics.currentValue > 0
                ? `${((metrics.equity / metrics.currentValue) * 100).toFixed(0)}% ownership`
                : "—"
              }
            </p>
          </div>

          {/* RENTAL INCOME or MONTHLY COST */}
          {metrics.rentalIncome > 0 ? (
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
              <p className="font-semibold text-purple-900 text-xs uppercase tracking-wide">Monthly Income</p>
              <p className="text-2xl font-bold mt-2 text-purple-900">
                ${metrics.rentalIncome.toLocaleString()}
              </p>
              <p className="text-xs text-purple-700 mt-1">
                Rental revenue
              </p>
            </div>
          ) : (
            <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200">
              <p className="font-semibold text-amber-900 text-xs uppercase tracking-wide">Monthly Cost</p>
              <p className="text-2xl font-bold mt-2 text-amber-900">
                {metrics.mortgage?.payment
                  ? `$${metrics.mortgage.payment.toLocaleString()}`
                  : "—"
                }
              </p>
              <p className="text-xs text-amber-700 mt-1">
                {metrics.mortgage?.renewalDate
                  ? `Renews ${new Date(metrics.mortgage.renewalDate).toLocaleDateString()}`
                  : "No mortgage"
                }
              </p>
            </div>
          )}

        </div>
      </div>

      {/* TAB CONTENT */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
        {activeTab === "overview" && (
          <div className="space-y-6">
            <OverviewTab property={property} />

            {/* MAP IN OVERVIEW TAB */}
            {metrics.lat && metrics.lng && (
              <div className="pt-6 border-t border-gray-200">
                <h2 className="text-xl font-semibold text-hn-dark mb-4">
                  Location & Nearby Amenities
                </h2>
                <PropertyMap
                  center={{ lat: metrics.lat, lng: metrics.lng }}
                  zoom={16}
                  showNearbyAmenities={true}
                  showStreetView={true}
                  properties={[property]}
                />
              </div>
            )}
          </div>
        )}

        {activeTab === "documents" && (
          <DocumentsTab propertyId={property.id} />
        )}

        {activeTab === "finances" && (
          <FinancesTab property={property} />
        )}

        {activeTab === "homerun" && (
          <HomeRunTab propertyId={property.id} propertyName={property.name || property.address} />
        )}

        {activeTab === "users" && (
          <UsersTab property={property} />
        )}

        {activeTab === "communication" && (
          <CommunicationTab property={property} />
        )}
      </div>
    </div>
  );
}
